import * as React from 'react';
import { useSearch, useNavigate } from '@tanstack/react-router';
import { useSharedFilters, buildNextSharedFilterSearch } from '../../hooks/use-shared-filters';
import { usePoiFilterParams } from '../../hooks/use-poi-filter-params';
import {
  useRangeFilterParams,
  buildNextRangeFilterValues,
} from '../../hooks/use-range-filter-params';
import {
  useNoPhotoParam,
  useAvailabilityRangeParams,
} from '../../features/media-units/hooks/use-catalog-sidecar-params';
import {
  CATALOG_ONLY_REAL_ARRAY_FIELD_IDS,
  CATALOG_ONLY_SCALAR_TEXT_FIELD_IDS,
} from '../../features/media-units/lib/filter-fields-catalog-only';
import { SHARED_FILTER_FIELD_IDS } from '../../lib/filter-config';
import { stripLatLng } from './strip-lat-lng';
import type { MapInstance } from '@ooh/ui/components/map-view';
import type { LinePopupState } from '../../features/transport-layers/components/transport-layer-overlay';

/**
 * Reads the catalog-only scalar-text fields (address/notes/roadNumber) into
 * FilterPanel's one-element-array value model. Unlike the catalog
 * (`validateMediaUnitsSearch`), `/map`'s own schema (`mapSearchSchema`)
 * persists these as a PLAIN STRING, not an array (see
 * `map-search-schema.test.ts`) — so these can't be read via the generic
 * `useSharedFilters(CATALOG_ONLY_ARRAY_FIELD_IDS)` the catalog uses, which
 * would look for (and never find) an array. material/multiformat/tags don't
 * need this: both routes persist those as real arrays.
 */
function readScalarTextFilterValues(search: Record<string, unknown>): Record<string, string[]> {
  const result: Record<string, string[]> = {};
  for (const id of CATALOG_ONLY_SCALAR_TEXT_FIELD_IDS) {
    const val = search[id];
    if (typeof val === 'string' && val) result[id] = [val];
  }
  return result;
}

/** Inverse of `readScalarTextFilterValues`: unwraps the one-element array back to a plain string. */
function buildNextScalarTextFilterSearch(
  current: Record<string, unknown>,
  value: Record<string, string[]>,
): Record<string, unknown> {
  const next = { ...current };
  for (const id of CATALOG_ONLY_SCALAR_TEXT_FIELD_IDS) {
    const trimmed = value[id]?.[0]?.trim();
    if (trimmed) next[id] = trimmed;
    else delete next[id];
  }
  return next;
}

// eslint-disable-next-line max-lines-per-function -- noPhoto/availabilityRange sidecar state (map-catalog-filter-parity) pushed this a few lines over budget
export function useMapPageState() {
  const [showFilters, setShowFilters] = React.useState(false);
  const navigate = useNavigate();
  // Combines the shared filter dimensions with the catalog-only array/text
  // selects and numeric ranges into ONE FilterPanel-facing value/setter pair,
  // mirroring the catalog's identical combinedFilterValue/handleCombinedFiltersChange
  // wiring (use-media-units-page.ts) — otherwise the 24 catalog-only fields now
  // rendered here (includeCatalogOnlyFields: true in use-map-pins-data.ts) would
  // read/write nothing (map-catalog-filter-parity).
  // structuralSharing: false required when the selected type is not JSON-safe —
  // same reasoning as useSharedFilters's own internal useSearch call.
  const rawSearch = useSearch({ strict: false, structuralSharing: false }) as Record<
    string,
    unknown
  >;
  const [sharedFilterValue] = useSharedFilters(SHARED_FILTER_FIELD_IDS);
  const [catalogArrayValue] = useSharedFilters(CATALOG_ONLY_REAL_ARRAY_FIELD_IDS);
  const [rangeValue] = useRangeFilterParams();
  const scalarTextValue = React.useMemo(() => readScalarTextFilterValues(rawSearch), [rawSearch]);
  const filterValue = React.useMemo(
    () => ({ ...sharedFilterValue, ...catalogArrayValue, ...scalarTextValue, ...rangeValue }),
    [sharedFilterValue, catalogArrayValue, scalarTextValue, rangeValue],
  );
  // Functional search updater: reads LIVE state at commit time instead of a
  // captured snapshot, so this never clobbers a sibling filter-state setter
  // (POI params, noPhoto, availability) that commits close to it — same race
  // buildNextSharedFilterSearch's doc comment describes.
  const setFilterValue = React.useCallback(
    (value: Record<string, string[]>) => {
      type AnyNavigate = (opts: {
        search: (prev: Record<string, unknown>) => Record<string, unknown>;
        replace: boolean;
      }) => void;
      void (navigate as unknown as AnyNavigate)({
        search: (prev) => {
          let next = buildNextSharedFilterSearch(prev, SHARED_FILTER_FIELD_IDS, value);
          next = buildNextSharedFilterSearch(next, CATALOG_ONLY_REAL_ARRAY_FIELD_IDS, value);
          next = buildNextScalarTextFilterSearch(next, value);
          next = buildNextRangeFilterValues(next, value);
          return next;
        },
        replace: true,
      });
    },
    [navigate],
  );
  const [poiFilterParams, setPoiFilterParams] = usePoiFilterParams();
  // noPhoto/availability sidecar state — reuses the catalog's own hooks (now
  // genuinely shared, not catalog-only, since MapPinsQuerySchema carries both
  // dimensions too — map-catalog-filter-parity) instead of duplicating the
  // URL-state logic.
  const [noPhoto, setNoPhoto] = useNoPhotoParam();
  const [availabilityRange, setAvailabilityRange] = useAvailabilityRangeParams();
  const [mapBbox, setMapBboxState] = React.useState<string | null>(null);
  const [visiblePoiCategories, setVisiblePoiCategories] = React.useState<Set<string>>(new Set());
  const [mapInstance, setMapInstance] = React.useState<MapInstance | null>(null);
  const [currentZoom, setCurrentZoom] = React.useState<number | null>(null);
  const [linePopup, setLinePopup] = React.useState<LinePopupState | null>(null);

  // Read the last-persisted viewport bbox once on mount (used to restore the
  // camera on arrival — see MapPage's initialBounds). `viewBbox` is a distinct
  // URL param from the `lat`/`lng`/`zoom` coordinate-search params (CU-86cb1nq31).
  const initialViewBbox = typeof rawSearch.viewBbox === 'string' ? rawSearch.viewBbox : undefined;

  // Persists the map viewport (not just internal query state) so filters AND
  // camera position both survive navigating away to a unit detail page and
  // back. Cast is necessary: navigate() without `from` has no concrete
  // search type — same pattern as setFilterValue above.
  //
  // Also strips lat/lng HERE, in the same atomic search update, rather than
  // in a separate mount-time effect (that was CU-86cb1nq31's original
  // approach and had a real bug: stripping on mount races the map's own
  // initial-camera setup — under React StrictMode's double-effect mount
  // cycle, the strip's navigate() could commit before MapView ever read
  // lat/lng to compute its initialCenter, so the map fell back to
  // DEFAULT_CENTER instead of centering on the coordinate deep link, with
  // no viewBbox yet established to fall back on either. onBoundsChange is
  // the correct signal instead: it only ever fires AFTER the map has
  // genuinely rendered its first camera position, so by the time this runs,
  // lat/lng have definitely already served their purpose.
  const setMapBbox = React.useCallback(
    (bbox: string) => {
      setMapBboxState(bbox);
      type AnyNavigate = (opts: {
        search: (prev: Record<string, unknown>) => Record<string, unknown>;
        replace: boolean;
      }) => void;
      void (navigate as unknown as AnyNavigate)({
        search: (prev) => ({ ...stripLatLng(prev), viewBbox: bbox }),
        replace: true,
      });
    },
    [navigate],
  );

  return {
    showFilters,
    setShowFilters,
    filterValue,
    setFilterValue,
    poiFilterParams,
    setPoiFilterParams,
    noPhoto,
    setNoPhoto,
    availabilityRange,
    setAvailabilityRange,
    mapBbox,
    setMapBbox,
    initialViewBbox,
    visiblePoiCategories,
    setVisiblePoiCategories,
    mapInstance,
    setMapInstance,
    currentZoom,
    setCurrentZoom,
    linePopup,
    setLinePopup,
  };
}
